import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../../services/api'

/** The role the advertiser gave the agent's service account in Google Ads. */
export type AccessLevel = 'full' | 'read_only' | 'no_access' | 'unknown'
export interface AccountAccess {
  access: AccessLevel
  checked_at: string
}
export type AccessMap = Record<string, AccountAccess | undefined>

// Mirrors MAX_ACCOUNTS_PER_AGENT / MAX_CAMPAIGNS_PER_AGENT in utils/skills/google_ads/optimizer.py.
export const MAX_ACCOUNTS_PER_AGENT = 25
export const MAX_CAMPAIGNS_PER_AGENT = 500

const CHUNK = 25

/** Access level per account, loaded after the campaign list so the list itself stays fast. */
export function useAccountAccess(customerIds: string[]) {
  const [access, setAccess] = useState<AccessMap>({})
  const [checking, setChecking] = useState<Set<string>>(new Set())

  const load = useCallback(async (ids: string[], refresh: boolean) => {
    if (!ids.length) return
    setChecking((prev) => new Set([...prev, ...ids]))
    for (let i = 0; i < ids.length; i += CHUNK) {
      const chunk = ids.slice(i, i + CHUNK)
      try {
        const res = await api.get('/api/google-ads/access-levels', {
          params: { ids: chunk.join(','), refresh: refresh ? 1 : 0 },
        })
        setAccess((prev) => ({ ...prev, ...(res.data as AccessMap) }))
      } catch {
        setAccess((prev) => ({
          ...prev,
          ...Object.fromEntries(chunk.map((id) => [id, { access: 'unknown', checked_at: '' }])),
        }))
      } finally {
        setChecking((prev) => {
          const next = new Set(prev)
          chunk.forEach((id) => next.delete(id))
          return next
        })
      }
    }
  }, [])

  const key = customerIds.join(',')
  const idsRef = useRef(customerIds)
  idsRef.current = customerIds
  useEffect(() => {
    load(customerIds, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, load])

  const recheck = useCallback((id: string) => load([id], true), [load])
  /** Bypass the server's one-hour cache, e.g. after an advertiser changed the agent's access. */
  const recheckAll = useCallback(() => load(idsRef.current, true), [load])
  return { access, checking, recheck, recheckAll }
}

export interface ModeSummary {
  text: string
  /** Every selected account gave read-only access, so Live mode can't do anything. */
  allReportOnly: boolean
  liveCount: number
  reportOnlyCount: number
}

function listNames(names: string[]): string {
  if (names.length <= 3) return names.join(', ').replace(/, ([^,]*)$/, ' and $1')
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/** One sentence saying what the agent will do in the selected accounts, used everywhere the mode is mentioned. */
export function summarizeMode(
  accounts: { id: string; name: string }[],
  access: AccessMap,
  safe: boolean,
): ModeSummary {
  const reportOnly = accounts.filter((a) => access[a.id]?.access === 'read_only')
  const live = accounts.length - reportOnly.length
  const allReportOnly = accounts.length > 0 && reportOnly.length === accounts.length
  const base = { allReportOnly, liveCount: safe ? 0 : live, reportOnlyCount: reportOnly.length }

  if (!accounts.length) {
    return { ...base, text: 'Select campaigns above to see what the agent will do.' }
  }
  if (safe || allReportOnly) {
    const why = !safe && allReportOnly ? ' All selected accounts gave report-only access.' : ''
    return {
      ...base,
      liveCount: 0,
      text: `Nothing will change in any account. You'll get recommendations for ${plural(accounts.length, 'account')}.${why}`,
    }
  }
  if (!reportOnly.length) {
    return {
      ...base,
      text: `Approved changes will be applied in ${accounts.length === 1 ? 'the selected account' : `all ${accounts.length} accounts`}, within the limits below.`,
    }
  }
  const names = listNames(reportOnly.map((a) => a.name))
  return {
    ...base,
    text:
      `Changes will be applied in ${plural(live, 'account')}. ${names} ` +
      `${reportOnly.length === 1 ? 'has' : 'have'} report-only access, so ` +
      `${reportOnly.length === 1 ? 'it gets' : 'they get'} recommendations only.`,
  }
}
